import type { Contact, Conversation, GroupRecord, MessageEvent } from "@veto/domain";
import {
  castVote,
  createEventId,
  createPoll,
  decodePollWire,
  encodePollWire,
  listPreview,
} from "@veto/domain";
import { CONTENT_TYPE_GROUP, CONTENT_TYPE_TEXT } from "@veto/protocol";
import { getMeta, upsertConversation } from "@veto/storage";
import { fanoutGroupMessage } from "../messaging/groupSend";
import type { Messenger } from "../messaging/messenger";

export function createPollActions(deps: {
  messenger: Messenger | null;
  activeId: string | null;
  activeConv: Conversation | null;
  activeContact: Contact | null;
  activeGroup: GroupRecord | null;
  contacts: Contact[];
  putMessage: (m: MessageEvent) => Promise<void>;
  setMessages: (m: MessageEvent[] | ((prev: MessageEvent[]) => MessageEvent[])) => void;
  setToast: (t: string | null) => void;
  refresh: () => Promise<void>;
}) {
  function directContact(): Contact | null {
    const c = deps.activeContact;
    if (!c || c.blockedAt || c.requestState === "pending" || c.verificationState === "changed")
      return null;
    return c;
  }

  async function createAndSendPoll(question: string, optionTexts: string[]) {
    const m = deps.messenger;
    if (!m || !deps.activeId) return;
    const poll = createPoll({ conversationId: deps.activeId, question, optionTexts });
    const body = encodePollWire(poll);
    const eventId = createEventId();
    const createdAt = Date.now();
    const isGroup = deps.activeConv?.type === "group" && deps.activeGroup;
    const contact = isGroup ? null : directContact();
    // 1:1 polls follow the chat's disappearing timer, as text does (phone parity).
    const timer = isGroup ? 0 : Number((await getMeta(`disappear.${deps.activeId}`)) ?? 0) || 0;
    if (!isGroup && !contact) {
      deps.setToast("Polls can't be sent in this chat right now");
      return;
    }
    const msg: MessageEvent = {
      eventId,
      conversationId: deps.activeId,
      senderDevice: m.selfDevice,
      direction: "out",
      body,
      logicalCounter: createdAt,
      createdAt,
      status: "queued",
      contentType: isGroup ? CONTENT_TYPE_GROUP : CONTENT_TYPE_TEXT,
      disappearTimerSec: timer || undefined,
      expiresAt: timer ? createdAt + timer * 1000 : undefined,
    };
    try {
      await deps.putMessage(msg);
      deps.setMessages((list) => [...list, msg]);
      if (deps.activeConv) {
        await upsertConversation({
          ...deps.activeConv,
          lastMessagePreview: listPreview(question) || "Poll",
          lastMessageAt: createdAt,
        });
      }
      await deps.refresh();
      const out = isGroup
        ? await fanoutGroupMessage({
            group: deps.activeGroup!,
            plaintext: body,
            contacts: deps.contacts,
            messenger: m,
            eventId,
            createdAt,
          })
        : await m.send({
            conversationId: deps.activeId,
            recipients: [contact!],
            contentType: CONTENT_TYPE_TEXT,
            body: timer ? `${timer}${body}` : body,
            eventId,
            createdAt,
            tracksMessage: true,
          });
      const next = { ...msg, status: out.status, transport: out.transport };
      await deps.putMessage(next);
      deps.setMessages((list) => list.map((x) => (x.eventId === eventId ? next : x)));
      deps.setToast("Poll sent");
    } catch (e) {
      deps.setToast(e instanceof Error ? e.message : "Poll failed");
    }
  }

  async function voteOnPoll(msg: MessageEvent, optionId: string) {
    const m = deps.messenger;
    if (!m) return;
    const poll = decodePollWire(msg.body);
    if (!poll) return;
    const nextPoll = castVote(poll, optionId, m.selfDevice);
    const body = encodePollWire(nextPoll);
    const next = { ...msg, body };
    await deps.putMessage(next);
    deps.setMessages((list) => list.map((x) => (x.eventId === msg.eventId ? next : x)));
    try {
      if (deps.activeConv?.type === "group" && deps.activeGroup) {
        await fanoutGroupMessage({
          group: deps.activeGroup,
          plaintext: body,
          contacts: deps.contacts,
          messenger: m,
          eventId: createEventId(),
          createdAt: Date.now(),
        });
        return;
      }
      const contact = directContact();
      if (contact && deps.activeId) {
        await m.send({
          conversationId: deps.activeId,
          recipients: [contact],
          contentType: CONTENT_TYPE_TEXT,
          body,
        });
      }
    } catch (e) {
      deps.setToast(e instanceof Error ? e.message : "Vote sync failed");
    }
  }

  return { createAndSendPoll, voteOnPoll };
}
